// The items a Briefing lists UNDER the kind row it has filtered to (frames 40007883:371407 and
// 40007892:377596).
//
// Two briefings ask the same question — "which of this thing's items are of that kind?" — about two
// different subjects: the ongoing story asks it of the whole story, an open Research Pack asks it of
// the pack alone. Answering it in two places is how the two lists would come to disagree about what
// an item is called or what "open" does with it, so it is answered once, here.
//
// Kept as a plain function over the story payload (no store, nothing mounted): a briefing has to stay
// a value the store can hand around, so this returns rows rather than closures — see briefings/rows.js.

import { buildStorySteps } from '@/lib/storySteps'

/**
 * Does this story step belong to Research Pack `packId`?
 *
 * Three ways it can, and all three count: it IS the pack's latest node (`packId`), it was created
 * inside the pack (`relPackId` — how a task raised in a pack is tagged), or it is one of the pack's
 * earlier nodes, which are only recognisable by their `pack:{id}:{k}` key. Same rule StoryView's
 * middle uses to narrow to a pack.
 */
export const stepInPack = (step, packId) => {
  const id = String(packId ?? '')
  return step?.packId === id || step?.relPackId === id || String(step?.key || '').startsWith(`pack:${id}:`)
}

/**
 * The active kind's items, as briefing rows.
 *
 * @param story    the ongoing story payload
 * @param children its sub-ongoings (buildStorySteps only knows the ones it is handed)
 * @param kind     the step kind in force — 'task' | 'note' | 'event' | 'sub' | …
 * @param packId   narrow to one Research Pack's items (the pack briefing's rows), or null for the
 *                 whole story
 * @returns `[{ key, title, svg, color, edit?, subId?, relationship? }]`, or null when there is
 *          nothing to list — a briefing carries the key only when it has a list, so every other
 *          briefing stays byte-for-byte the value it was.
 */
export function kindItems(story, children, kind, packId = null) {
  if (!story || !kind) return null

  const rows = buildStorySteps(story, children || [])
    .filter(st => st.stepKind === kind && st.key !== 'next')
    .filter(st => !packId || stepInPack(st, packId))
    // The step's own icon and colour, and what "open" can do with it: an `edit` payload puts the
    // entity in the SmartBox (tasks/notes/events), a subId navigates to the sub-ongoing.
    .map(st => ({
      key: st.key,
      title: st.title || '',
      svg: st.svg,
      color: st.color,
      ...(st.edit ? { edit: st.edit } : {}),
      ...(st.subId ? { subId: st.subId } : {}),
      // Where the item was raised — "prada sunglasses" under a task says the task was created
      // inside that pack (frame 40007883:371175). Dropped inside a pack briefing, where every row
      // names the same pack and the line would only repeat the title above it.
      ...(!packId && st.relationship ? { relationship: st.relationship } : {}),
    }))

  return rows.length ? rows : null
}

export default kindItems
